import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { useAuth } from './AuthContext';
import { resolveCompanyBranding, BRANDING_ALL_VAR_KEYS } from '../utils/branding';

const BrandingContext = createContext(null);

const USE_BOOTSTRAP = Symbol('branding-use-bootstrap');

/** CSS variable overrides applied on top of branding tokens when dark mode is active. */
const DARK_MODE_OVERRIDES = {
  '--branding-app-surface': '#0a0a0a',
  '--branding-app-surface-panel': '#111111',
  '--branding-app-surface-panel-fg': '#f5f5f5',
  '--branding-app-surface-panel-sub': '#a3a3a3',
  '--branding-app-surface-panel-muted': '#737373',
  '--branding-app-surface-panel-hint': '#525252',
  '--branding-app-surface-panel-border': 'rgba(255, 255, 255, 0.08)',
  '--branding-sidebar-bg': '#0a0a0a',
};

export function applyDarkModeOverrides(root) {
  for (const [k, v] of Object.entries(DARK_MODE_OVERRIDES)) {
    root.style.setProperty(k, v);
  }
}

export function removeDarkModeOverrides(root) {
  for (const k of Object.keys(DARK_MODE_OVERRIDES)) {
    root.style.removeProperty(k);
  }
}

/**
 * Module-level refs so ThemeContext can apply/restore the primary override on theme toggle
 * without needing a React dependency on BrandingContext tokens.
 */
const _darkPrimary = { primary: null, hover: null, fg: null };
const _lightPrimary = { primary: null, hover: null, fg: null };

export function applyDarkModePrimaryOverride(root) {
  if (!_darkPrimary.primary) return;
  root.style.setProperty('--branding-app-primary', _darkPrimary.primary);
  root.style.setProperty('--branding-app-primary-hover', _darkPrimary.hover);
  root.style.setProperty('--branding-app-primary-fg', _darkPrimary.fg);
}

export function restoreLightPrimary(root) {
  if (!_lightPrimary.primary) return;
  root.style.setProperty('--branding-app-primary', _lightPrimary.primary);
  root.style.setProperty('--branding-app-primary-hover', _lightPrimary.hover);
  root.style.setProperty('--branding-app-primary-fg', _lightPrimary.fg);
}

function applyTokensToDocument(root, tokens) {
  if (!tokens) return;
  for (const [cssVar, key] of BRANDING_ALL_VAR_KEYS) {
    const v = tokens[key];
    if (v != null) root.style.setProperty(cssVar, v);
  }
}

function clearBrandingVars(root) {
  for (const [cssVar] of BRANDING_ALL_VAR_KEYS) {
    root.style.removeProperty(cssVar);
  }
}

/**
 * Applies company branding from Auth bootstrap + optional refetch (e.g. after saving in editor).
 *
 * Firebase/React pattern: load `companies/{id}` in the same auth bootstrap as `users/{uid}` before
 * `setLoading(false)`, so the shell mounts with `companyBootstrap` and tokens already resolved.
 */
export function BrandingProvider({ children }) {
  const { user, userData, companyBootstrap, loading: authLoading } = useAuth();

  /** Overrides bootstrap after refetchCompanyBranding; cleared when session / bootstrap identity changes. */
  const [companyOverride, setCompanyOverride] = useState(USE_BOOTSTRAP);

  useLayoutEffect(() => {
    setCompanyOverride(USE_BOOTSTRAP);
  }, [user?.uid, userData?.companyId, companyBootstrap]);

  const company = useMemo(() => {
    if (authLoading || !user || !userData?.companyId) return null;
    if (companyOverride !== USE_BOOTSTRAP) return companyOverride;
    return companyBootstrap ?? null;
  }, [authLoading, user, userData?.companyId, companyBootstrap, companyOverride]);

  const tokens = useMemo(() => resolveCompanyBranding(company), [company]);

  useEffect(() => {
    const root = document.documentElement;
    if (authLoading || !user) {
      clearBrandingVars(root);
    } else {
      applyTokensToDocument(root, tokens);
      // Keep module refs in sync so ThemeContext can apply the right primary on toggle.
      _lightPrimary.primary = tokens.appPrimary;
      _lightPrimary.hover = tokens.appPrimaryHover;
      _lightPrimary.fg = tokens.appPrimaryFg;
      _darkPrimary.primary = tokens.appPrimaryDarkMode;
      _darkPrimary.hover = tokens.appPrimaryDarkModeHover;
      _darkPrimary.fg = tokens.appPrimaryDarkModeFg;
    }
    // Re-apply dark mode on top of branding — dark overrides inline styles set above.
    if (root.dataset.theme === 'dark') {
      applyDarkModeOverrides(root);
      applyDarkModePrimaryOverride(root);
    }
    return undefined;
  }, [authLoading, user, tokens]);

  const loading = authLoading;

  const refetchCompanyBranding = useCallback(async () => {
    if (!user?.uid || !userData?.companyId || authLoading) return;
    try {
      const snap = await getDoc(doc(db, 'companies', userData.companyId));
      setCompanyOverride(snap.exists() ? snap.data() : null);
    } catch (e) {
      console.error('BrandingProvider: refetch failed', e);
    }
  }, [user?.uid, userData?.companyId, authLoading]);

  const value = useMemo(
    () => ({
      tokens,
      company,
      companyId: userData?.companyId ?? null,
      loading,
      hasCustomPlanBarColor: tokens.hasCustomPlanBarColor,
      refetchCompanyBranding,
    }),
    [tokens, company, userData?.companyId, loading, refetchCompanyBranding],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    throw new Error('useBranding must be used within BrandingProvider');
  }
  return ctx;
}
